import React, { useState, useEffect, useRef } from "react"
import DataLoader, { factors } from "../DataLoader"

import { scaleLinear } from "d3-scale"
import gradient from "../utils/gradient"

import "./GlobalInsightsRanking.scss"
import { globalInsightsConstants } from "./"
import Tooltip from "../Tooltip/"
import { useTransition, animated } from "react-spring"
import { useSmallWidthMode } from "../utils/smallWidthMode"
import getShortCountryLabel from "../utils/getShortCountryLabel"

export default function GlobalInsightsTable(props) {
  const {
    selectedYear,
    selectedFactor,
    containerDimensions,
    selectedFilterIncome,
    selectedFilterRegion,
    selectedCountry,
    headerHeight,
  } = props
  const [tooltipData, setTooltipData] = useState()
  const selectedCountryRef = useRef()
  const inSmallWidthMode = useSmallWidthMode()
  useEffect(() => {
    if (
      selectedCountry !== "" &&
      selectedCountryRef.current &&
      props.containerRef &&
      headerHeight
    ) {
      const scrollDomNode = inSmallWidthMode
        ? document.documentElement
        : props.containerRef
      const scrollOffset =
        selectedCountryRef.current.getBoundingClientRect().y +
        scrollDomNode.scrollTop -
        185 // 185 is about the size of the combined headers
      scrollDomNode.scrollTo({
        top: scrollOffset,
        behavior: "smooth",
      })
    }
  }, [selectedCountry, props.containerRef, inSmallWidthMode, headerHeight])

  let scrollToCountryNeeded = useRef(false)
  useEffect(() => {
    scrollToCountryNeeded.current = true
  }, [selectedFactor, selectedYear])

  let countries = [...DataLoader.dataByYear[selectedYear].countries]

  if (selectedFilterIncome !== "") {
    countries = countries.filter(
      (country) => country["Income Group"] === selectedFilterIncome,
    )
  }
  if (selectedFilterRegion !== "") {
    countries = countries.filter(
      (country) => country.Region === selectedFilterRegion,
    )
  }

  if (props.overrideRegionFilter) {
    countries = countries.filter(props.overrideRegionFilter)
  }

  const factorSelected = selectedFactor !== ""
  const factorIndex = factorSelected ? factors.indexOf(selectedFactor) : -1

  const {
    rowHeight,
    labelRankPadding,
    labelRankWidth,
    sidePadding,
    barHeight,
  } = globalInsightsConstants

  const svgHeight = countries.length * rowHeight
  const sidePaddingToUse = inSmallWidthMode ? labelRankPadding : sidePadding
  const svgWidth =
    (containerDimensions.width ? containerDimensions.width : 300) -
    sidePaddingToUse
  const scoreKey = factorSelected
    ? `Factor ${factorIndex + 1}: ${selectedFactor}`
    : "overall"
  const sortedCountries = [...countries]
  sortedCountries.sort((a, b) => {
    // if (selectedSortOrder === 'alphabetical') {
    //   if (a.Country > b.Country) {
    //     return 1
    //   }
    //   return -1
    // }
    return b[scoreKey] - a[scoreKey]
  })
  const countryIndicies = {}
  sortedCountries.forEach((country, index) => {
    countryIndicies[country.Country] = index
  })

  const maxValue = sortedCountries[0][scoreKey]

  const labelWidth = Math.max(
    svgWidth * globalInsightsConstants.labelWidthPercentage,
    globalInsightsConstants.minLabelWidth,
  )
  const valueLabelWidth = 30 + globalInsightsConstants.labelRankPadding * 2
  const fullBarWidth = svgWidth - labelWidth - valueLabelWidth

  const xScale = scaleLinear().domain([0, maxValue]).range([0, fullBarWidth])
  const hoverCountry = (country) => {
    return (event) => {
      if (!country) {
        return setTooltipData(null)
      }
      let x = 0
      let y = 0
      if ("clientX" in event) {
        x = event.clientX
        y = event.clientY
      }
      if (event.touches) {
        event.preventDefault()
        event.stopPropagation()
      }
      setTooltipData({ country, x, y })
    }
  }

  const getSpringValues = (country) => {
    const score = country[scoreKey]
    const fill = gradient(score)
    const width = xScale(score.toFixed(2))
    const y = countryIndicies[country.Country] * rowHeight

    return {
      score,
      fill,
      width,
      y,
    }
  }
  const totalTransitions = countries.length
  let numberTransitionsCompleted = 0
  const transitions = useTransition(countries, (d) => d.Country, {
    enter: getSpringValues,
    update: getSpringValues,
    onRest: () => {
      numberTransitionsCompleted++
      if (numberTransitionsCompleted === totalTransitions) {
        if (scrollToCountryNeeded.current) {
          scrollToCountryNeeded.current = false
          if (
            selectedCountry !== "" &&
            selectedCountryRef.current &&
            props.containerRef
          ) {
            const scrollOffset =
              selectedCountryRef.current.getBoundingClientRect().y +
              props.containerRef.scrollTop -
              185 // 185 is about the size of the combined headers
            props.containerRef.scrollTo({
              top: scrollOffset,
              behavior: "smooth",
            })
          }
        }
      }
    },
  })

  const countryGroups = transitions.map((transition, countryIndex) => {
    if (transition.state === "leave") {
      return null
    }
    const country = transition.item
    const { score, fill, width, y } = transition.props

    const textY = barHeight * 0.75
    const bgRect =
      countryIndicies[country.Country] % 2 === 0 ? (
        <rect
          fill="#f2f2f2"
          width={labelWidth + fullBarWidth + valueLabelWidth - sidePaddingToUse}
          x={sidePaddingToUse}
          height={rowHeight}
        />
      ) : null
    const topMargins = (rowHeight - barHeight) / 2
    const countryLabelWeight =
      country.Country === selectedCountry ? "900" : null
    const countryOpacity =
      selectedCountry !== ""
        ? selectedCountry === country.Country
          ? 1
          : 0.8
        : 1
    const countryTextStyle = {
      fontWeight: countryLabelWeight,
      cursor: "pointer",
    }
    let countryLabel = country.Country
    if (props.labelSubstitutions && props.labelSubstitutions[countryLabel]) {
      countryLabel = props.labelSubstitutions[countryLabel]
    }
    return (
      <animated.g
        key={country.Country}
        transform={y.interpolate((y) => `translate(0, ${y})`)}
        opacity={countryOpacity}
        ref={selectedCountry === country.Country ? selectedCountryRef : null}
      >
        {bgRect}
        <g transform={`translate(0, ${topMargins})`}>
          <animated.rect
            className="dataRect"
            fill={fill}
            x={labelWidth}
            width={width}
            height={barHeight}
            onMouseOver={hoverCountry(country)}
            onMouseOut={hoverCountry(null)}
            onMouseMove={hoverCountry(country)}
            onClick={props.gotoCountryPage(country.Country)}
            onTouchEnd={hoverCountry(country)}
          />
          <text
            className="textLabel"
            onClick={() => props.updateUrl("country")(country.Country)}
            x={labelWidth - labelRankPadding}
            y={textY}
            style={countryTextStyle}
          >
            {countryIndicies[country.Country] + 1}
          </text>
          <text
            className="textLabel"
            onClick={() => props.updateUrl("country")(country.Country)}
            x={labelWidth - labelRankPadding * 2 - labelRankWidth}
            y={textY}
            style={countryTextStyle}
          >
            {countryLabel}
          </text>
          <text
            className="textLabel smallWidthMode"
            onClick={() => props.updateUrl("country")(country.Country)}
            x={labelWidth - labelRankPadding}
            y={textY}
            style={countryTextStyle}
          >
            {getShortCountryLabel(country.Country)}{" "}
            {countryIndicies[country.Country] + 1}
          </text>

          <animated.text
            x={width.interpolate(
              (width) => width + labelWidth + labelRankPadding,
            )}
            y={textY}
            style={{ textAnchor: "start", ...countryTextStyle }}
          >
            {score.interpolate((score) => score.toFixed(2))}
          </animated.text>
        </g>
      </animated.g>
    )
  })

  return (
    <div className="globalInsightsRanking">
      <div className="graphTitle" style={{ marginLeft: sidePaddingToUse }}>
        Countries Ranked by {factorSelected ? selectedFactor : "Overall Score"}
      </div>
      <svg height={svgHeight} width={svgWidth} style={props.style}>
        {countryGroups}
      </svg>
      {tooltipData ? (
        <Tooltip
          {...tooltipData}
          scoreKey={scoreKey}
          selectedYear={selectedYear}
          closeTooltip={hoverCountry(null)}
          gotoDetails={props.gotoCountryPage(tooltipData.country.Country)}
        />
      ) : null}
    </div>
  )
}
